import {Injectable} from "@angular/core";
import {HttpHeaders, HttpClient} from "@angular/common/http";
import {Router} from "@angular/router";
import { JwtHelperService } from "@auth0/angular-jwt";
import {environment} from "../../../environments/environment";
import {Token} from "../../models/token";
import {Observable} from "rxjs";
import {User} from "../../models/user";
import { map } from 'rxjs/operators';

@Injectable()
export class CasService {
  public baseUrl: string = environment.CAS_URL;
  public partcUrl: string = environment.PARTC_URL;
  public OAuthRedirectUrl: string = environment.OAUTH_REDIRECT_URL;
  public OAuthClientId: string = environment.OAUTH_CLIENT_ID;
  public OAuthSecret: string = environment.OAUTH_SECRET;
  public jwtHelper: JwtHelperService = new JwtHelperService();
  private subscribers: Array<() => void> = [];
  private roleCache: any = {};

  constructor(private http: HttpClient, private router: Router) {}

  private static savedToken() {
    return localStorage.getItem(Token.tokeNameInSotage);
  }

  private static saveToken(token) {
    localStorage.setItem(Token.tokeNameInSotage, token);
  }

  private static savedRefreshToken() {
    return localStorage.getItem(Token.refreshTokenNameInStorage);
  }

  private static saveRefreshToken(token) {
    localStorage.setItem(Token.refreshTokenNameInStorage, token);
  }

  private static removeTokens() {
    localStorage.removeItem(Token.tokeNameInSotage);
    localStorage.removeItem(Token.refreshTokenNameInStorage);
  }

  private static getAuthHeader() {
    return {
      headers: new HttpHeaders()
          .append("Authorization", "Bearer " + CasService.savedToken()),
    };
  }

  private saveAuthToken(token, response) {
    if (token === null || token === "null") {
      this.router.navigate(["/login"]);
    }
    CasService.saveToken(token);
    this.notifySubscribers();
  }

  public async getToken() {
    // lets check if token is about to expire or already expired before using it
    if (this.savedTokenIsExpiredFor20Hours()) {
      // refresh token probably invalid also
      console.error("Session is expired");
      await this.logout("/login");
      return Promise.reject();
    }
    if (this.savedTokenIsAboutToExpireIn5Minutes()) {
      await this.refreshToken();
    }
    return Promise.resolve(CasService.savedToken());
  }

  public login(url: string) {
    window.location.href = url;
  }

  public accessToken(authenticationCode: string) {
    return this.http.post(this.baseUrl + "access_token",
      this.jsonToQueryString({
        grant_type: "authorization_code",
        code: authenticationCode,
        redirect_uri: this.OAuthRedirectUrl,
      }),
      {
        headers: new HttpHeaders()
            .append("Authorization", "Basic " + btoa(this.OAuthClientId + ":" + this.OAuthSecret))
            .append("Content-Type", "application/x-www-form-urlencoded"),
      },
    ).pipe(map((res) => this.saveAccessAndRefreshToken(res)));
  }

  public queryAdditionalUserInformation(): Observable<User> {
    return this.http.get(this.partcUrl + "user/",
        {
          headers: new HttpHeaders()
              .append("Authorization", "Bearer " + CasService.savedToken()),
        }) as Observable<User>;
  }

  public async isUserInRole(role: string): Promise<boolean> {
    if (this.roleCache.hasOwnProperty(role)) { return Promise.resolve(this.roleCache[role]); }
    const data = await this.queryAdditionalUserInformation().toPromise();
    this.roleCache[role] = data.roles.indexOf(role) > -1;
    return Promise.resolve(this.roleCache[role]);
  }

  public async getUserRoles(): Promise<string[]> {
    return (await this.queryAdditionalUserInformation().toPromise()).roles;
  }

  public async refreshToken() {
    try {
      const response = await this.http.post(this.baseUrl + "access_token",
          this.jsonToQueryString({
            grant_type: "refresh_token",
            refresh_token: CasService.savedRefreshToken(),
            redirect_uri: this.OAuthRedirectUrl,
          }),
          {
            headers: new HttpHeaders()
                .append("Authorization", "Basic " + btoa(this.OAuthClientId + ":" + this.OAuthSecret))
                .append("Content-Type", "application/x-www-form-urlencoded"),
          },
      ).toPromise();
      return this.saveAccessAndRefreshToken(response);
    } catch (e) {
      console.log("error from refreshToken flow", e);
      return await this.logout("/login");
    }
  }

  public subscribeForTokenChange(subscriber: () => void) {
    this.subscribers.push(subscriber);
  }

  public notifySubscribers() {
    this.subscribers.forEach((it) => it());
  }

  public unsubscribe(subscriber: () => void) {
    this.subscribers = this.subscribers.filter((sub) => {
      return sub !== subscriber;
    });
  }

  public isLoggedIn() {
    return this.isSavedTokenValid();
  }

  public loggedUser() {
    if (!this.isSavedTokenValid()) {
      throw new Error("User is not logged in");
    }
    return this.savedUser();
  }

  private  logoutCb(redirectUrl) {
    CasService.removeTokens();
    window.location.href = redirectUrl;
  }

  public async logout(redirectUrl) {
    const authHeader = CasService.getAuthHeader();
    try {
      await this.http.get(this.baseUrl + "userdata/invalidateToken", authHeader).toPromise();
    } catch (e) {
      // logout failed delete token and direct user to login anyway
    }
    return this.logoutCb(redirectUrl);
  }

  public isSavedTokenValid() {
    const token = CasService.savedToken();
    return token && this.isValid(token);
  }

  public isAnyTokenPresent() {
    const token = CasService.savedToken();
    return token && token.length > 0;
  }

  public getSearchParameters(): any {
    const prmstr = window.location.search.substr(1);
    return prmstr && prmstr !== "" ? this.transformToAssocArray(prmstr) : {};
  }

  public savedTokenHasExpired() {
    return this.jwtHelper.isTokenExpired(CasService.savedToken());
  }

  public savedTokenIsAboutToExpireIn5Minutes() {
    return this.jwtHelper.isTokenExpired(CasService.savedToken(), 60 * 5);
  }

  public savedTokenIsExpiredFor20Hours() {
    return this.jwtHelper.isTokenExpired(CasService.savedToken(), -(60 * 60 * 20));
  }

  public saveAccessAndRefreshToken(response: any) {
    const token = response.access_token;
    if (this.isValid(token)) {
      this.saveAuthToken(token, response);
      CasService.saveRefreshToken(response.refresh_token);
      return true;
    }
    return false;
  }

  private transformToAssocArray(prmstr): object {
    const params = {};
    const prmarr = prmstr.split("&");
    prmarr.forEach((it) => {
      const tmparr = it.split("=");
      params[tmparr[0]] = tmparr[1];
    });
    return params;
  }

  private jsonToQueryString(json: any) {
    return Object.keys(json).map((key) => {
      return encodeURIComponent(key) + "=" +
        encodeURIComponent(json[key]);
    }).join("&");
  }

  private isValid(token) {
    try {
      const user = this.parseToken(token);
      return token !== undefined && user !== undefined && !this.jwtHelper.isTokenExpired(token);
    } catch (e) {
      return false;
    }
  }

  private parseToken(token) {
    return this.jwtHelper.decodeToken(token);
  }

  private savedUser() {
    return this.parseToken(CasService.savedToken());
  }

}
